import type { Card } from "@amzn/ads-blink-client";
import type { SortOptions } from "@amzn/ads-blink-client";
import type {
    Content,
    Provider,
    Scope,
} from "@amzn/ads-discovery-theme-service-typescript-client";
import type { Providers } from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import type {
    InsightFamily,
    StandaloneRecommendation,
} from "@amzn/d16-g-client-deck-builder-typescript-client";

import type { SearchSortAttribute } from "../../api/adsApi/adsApi.interfaces";
import type { FilterButtonActionButton } from "../../components/discovery/filters/FilterButton";
import {
    defaultContentType,
    getDefaultContentSubType,
} from "../../config/filters.config";
import type { FeatureFlags } from "../../store/featureFlags";
import type { IHubContentSubType } from "../ihubContentType";
import { IHubContentType } from "../ihubContentType";
import { TopNavBarTabs } from "../navigation";
import { IHubContentProvider } from "../providers";

import {
    type GeneratedInsightsFilterOptions,
    type GeneratedInsightsFieldSelections,
    GeneratedInsightsFilterField,
} from "./generatedInsights";
import {
    type InsightFamilyFilterOptions,
    type InsightFamilyFieldSelections,
    InsightFamiliesFilterField,
} from "./insightFamilies";
import type {
    InsightFromAIFieldSelections,
    InsightFromAIFilterOptions,
} from "./insightFromAI";
import { InsightsFromAIFilterField } from "./insightFromAI";
import {
    type InsightPackagesFilterOptions,
    type InsightPackagesFilterSelections,
    InsightPackagesFilterField,
} from "./insightPackages";
import {
    type MarketingNarrativesFilterOptions,
    type MarketingNarrativesFieldSelections,
    MarketingNarrativesFilterField,
} from "./marketingNarratives";
import {
    type OnDemandDecksFilterOptions,
    type OnDemandDecksFilterFieldSelections,
    OnDemandDecksFilterField,
} from "./onDemandDeckTemplates";
import type {
    AllUnifiedFilterKeys,
    CoreUnifiedFilterKeys,
    UnifiedFilterKeys,
    UnifiedFilterOptions,
    UnifiedFilterSelections,
    UnifiedTypeGroups,
} from "./unified";
import { UnifiedFilterField } from "./unified";

export type DiscoveryFilterOptions =
    | InsightPackagesFilterOptions
    | MarketingNarrativesFilterOptions
    | OnDemandDecksFilterOptions
    | GeneratedInsightsFilterOptions
    | InsightFamilyFilterOptions
    | InsightFromAIFilterOptions;

export type DiscoveryFilterField =
    | InsightPackagesFilterField
    | MarketingNarrativesFilterField
    | OnDemandDecksFilterField
    | GeneratedInsightsFilterField
    | InsightFamiliesFilterField
    | InsightsFromAIFilterField
    | UnifiedFilterField;

export type DiscoveryFieldSelection =
    | InsightPackagesFilterSelections
    | MarketingNarrativesFieldSelections
    | OnDemandDecksFilterFieldSelections
    | GeneratedInsightsFieldSelections
    | InsightFamilyFieldSelections
    | InsightFromAIFieldSelections;

export interface DiscoveryFilter {
    field: DiscoveryFilterField;
    label: string;
    isAdditional?: boolean;
    groupConfiguration?: Record<string, DiscoveryFilterGroupConfiguration>;
    additionalActionButtons?: FilterButtonActionButton[];
}

export interface DiscoveryFilterGroupConfiguration {
    disabled?: boolean;
    featureFlag?: string;
    priority?: number;
    actionButton?: {
        label: string;
        onClick: () => void;
    };
}

export const FieldToTypeMap = {
    // Unified
    [UnifiedFilterField.BRAND]: IHubContentProvider.UNIFIED,
    [UnifiedFilterField.BROWSE_NODE]: IHubContentProvider.UNIFIED,
    [UnifiedFilterField.OBJECTIVE]: IHubContentProvider.UNIFIED,
    [UnifiedFilterField.TOPIC]: IHubContentProvider.UNIFIED,
    [UnifiedFilterField.LANGUAGE]: IHubContentProvider.UNIFIED,
    // insight families
    [InsightFamiliesFilterField.BRAND]: IHubContentProvider.INSIGHT_FAMILIES,
    [InsightFamiliesFilterField.BROWSE_NODE]:
        IHubContentProvider.INSIGHT_FAMILIES,
    [InsightFamiliesFilterField.OBJECTIVE]:
        IHubContentProvider.INSIGHT_FAMILIES,
    // explore with AI
    [InsightsFromAIFilterField.CONTENT_TYPES]:
        IHubContentProvider.INSIGHTS_FROM_AI,
    [InsightsFromAIFilterField.BRAND]: IHubContentProvider.INSIGHTS_FROM_AI,
    [InsightsFromAIFilterField.BROWSE_NODE]:
        IHubContentProvider.INSIGHTS_FROM_AI,
    // insight packages
    [InsightPackagesFilterField.BRAND]: IHubContentProvider.PACKAGES,
    [InsightPackagesFilterField.BROWSE_NODE]: IHubContentProvider.PACKAGES,
    [InsightPackagesFilterField.OBJECTIVE]: IHubContentProvider.PACKAGES,
    // marketing narratives
    [MarketingNarrativesFilterField.MARKETPLACE]:
        IHubContentProvider.MARKETING_NARRATIVES,
    [MarketingNarrativesFilterField.LANGUAGE]:
        IHubContentProvider.MARKETING_NARRATIVES,
    [MarketingNarrativesFilterField.VERTICAL]:
        IHubContentProvider.MARKETING_NARRATIVES,
    [MarketingNarrativesFilterField.SUBVERTICAL]:
        IHubContentProvider.MARKETING_NARRATIVES,
    [MarketingNarrativesFilterField.AD_ACTIVATION_CHANNEL]:
        IHubContentProvider.MARKETING_NARRATIVES,
    [MarketingNarrativesFilterField.AD_PRODUCTS_AND_FORMATS]:
        IHubContentProvider.MARKETING_NARRATIVES,
    [MarketingNarrativesFilterField.PRIMARY_CONTENT_CREATOR_TEAM]:
        IHubContentProvider.MARKETING_NARRATIVES,
    [MarketingNarrativesFilterField.MARKETING_STRATEGY]:
        IHubContentProvider.MARKETING_NARRATIVES,
    [MarketingNarrativesFilterField.SEASONAL_EVENTS]:
        IHubContentProvider.MARKETING_NARRATIVES,
    [MarketingNarrativesFilterField.ADVERTISER_GOAL]:
        IHubContentProvider.MARKETING_NARRATIVES,
    [MarketingNarrativesFilterField.FILE_TYPE]:
        IHubContentProvider.MARKETING_NARRATIVES,
    // on-demand insight decks
    [OnDemandDecksFilterField.TOPIC]: IHubContentProvider.TEMPLATES,
    [OnDemandDecksFilterField.LANGUAGE]: IHubContentProvider.TEMPLATES,
    // generated insights
    [GeneratedInsightsFilterField.INSIGHT_TYPE]:
        IHubContentProvider.GENERATED_INSIGHTS,
    [GeneratedInsightsFilterField.BRAND]:
        IHubContentProvider.GENERATED_INSIGHTS,
    [GeneratedInsightsFilterField.BROWSE_NODE]:
        IHubContentProvider.GENERATED_INSIGHTS,
};

export const TabToDefaultTypeMap: Record<
    TopNavBarTabs,
    { contentType: IHubContentType; contentSubType: () => IHubContentSubType }
> = {
    [TopNavBarTabs.ALL_INSIGHTS]: {
        contentType: defaultContentType,
        contentSubType: () => getDefaultContentSubType(defaultContentType),
    },
    [TopNavBarTabs.YOUR_INSIGHT_DECKS]: {
        contentType: defaultContentType,
        contentSubType: () => getDefaultContentSubType(defaultContentType),
    },
    [TopNavBarTabs.EXPLORE_WITH_AI]: {
        contentType: IHubContentType.INSIGHTS,
        contentSubType: () => ({
            provider: IHubContentProvider.INSIGHTS_FROM_AI,
        }),
    },
};

export interface DiscoveryInsightsFilters {
    [IHubContentProvider.UNIFIED]: {
        fieldSelections: UnifiedFilterSelections;
        filterOptions: UnifiedFilterOptions;
        data: Card[];
        visibleFilters: DiscoveryFilter[];
        additionalFilters: DiscoveryFilter[];
        searchQuery: string;
    };
    [IHubContentProvider.INSIGHT_FAMILIES]: {
        fieldSelections: InsightFamilyFieldSelections;
        typeOptions: string[];
        filterOptions: InsightFamilyFilterOptions;
        data: InsightFamily[];
        visibleFilters: DiscoveryFilter[];
        additionalFilters: DiscoveryFilter[];
        searchQuery: string;
    };
    [IHubContentProvider.INSIGHTS_FROM_AI]: {
        fieldSelections: InsightFromAIFieldSelections;
        filterOptions: InsightFromAIFilterOptions;
        data: InsightFamily[]; // only for filter use
        visibleFilters: DiscoveryFilter[];
        additionalFilters: DiscoveryFilter[];
    };
    [IHubContentProvider.PACKAGES]: {
        fieldSelections: InsightPackagesFilterSelections;
        filterOptions: InsightPackagesFilterOptions;
        data: StandaloneRecommendation[];
        visibleFilters: DiscoveryFilter[];
        additionalFilters: DiscoveryFilter[];
        searchQuery: string;
    };
    [IHubContentProvider.TEMPLATES]: {
        fieldSelections: OnDemandDecksFilterFieldSelections;
        filterOptions: OnDemandDecksFilterOptions;
        // TODO: update once we have type
        data: any[];
        visibleFilters: DiscoveryFilter[];
        additionalFilters: DiscoveryFilter[];
        searchQuery: string;
    };
}

export interface DiscoveryMarketingContentFilters {
    [IHubContentProvider.MARKETING_NARRATIVES]: {
        fieldSelections: MarketingNarrativesFieldSelections;
        filterOptions: MarketingNarrativesFilterOptions;
        data: Content[];
        visibleFilters: DiscoveryFilter[];
        additionalFilters: DiscoveryFilter[];
        searchQuery: string;
        sortAttribute: SearchSortAttribute;
    };
}

export type DiscoveryFilters =
    | DiscoveryInsightsFilters
    | DiscoveryMarketingContentFilters;

export interface DiscoveryContentFilterState {
    contentSubType: IHubContentSubType;
    filters: DiscoveryFilters;
}

export interface DiscoveryContentType {
    contentType: IHubContentType;
    scopeToLabel: { [key: string]: string };
}

export type DiscoveryFilterState = DiscoveryContentType & {
    [key in IHubContentType]: DiscoveryContentFilterState;
};

// [TO-DO]: need to update this from api response, instead of UI types (will be doing after UAT)
export interface ContentSubTypeFilterLeafItem extends Scope {
    fetchInfo: {
        scope: Scope;
        provider: Provider;
    };
}

export interface ContentSubTypeFilterGroupItem {
    label: string;
    items: ContentSubTypeFilterLeafItem[];
}

// Below this comment are the new objects used for unified search.
// Once unified search is fully released, we can begin removing code references above this comment

export type UnifiedFilters = {
    [K in AllUnifiedFilterKeys]: FilterValues<K>;
};

export type UnifiedFilterState = {
    searchQuery: string;
    filters: UnifiedFilters;
    isContentDirty: boolean;
    isMetadataDirty: boolean;
    initialVisibleFilters: UnifiedFilterKeys[];
    lastSearchParameters: LastSearchFilter;
    lastSearchResultTotalCount: number;
    sortAttribute: SortOptions;
};

// The following interfaces are used to store information about how filters are being used
export interface IBaseFilterOptionModel {
    id: string;
    label: string;
    tooltip: string;
    tag?: FilterTag;
    isSelected: boolean;
    isDisabled?: boolean;
    enablingFeatureFlag?: FeatureFlags;
}

export interface ITypeFilterOptionModel extends IBaseFilterOptionModel {
    group: UnifiedTypeGroups;
    scopes: Providers[];
}

export type TypeFilterOptionModel = ITypeFilterOptionModel;
export type BaseFilterOptionModel = IBaseFilterOptionModel;

export type AllFilterOptionModelType =
    | BaseFilterOptionModel
    | TypeFilterOptionModel;

export type AllFilterOptionId = BaseFilterOptionModel["id"];

type FilterTag = {
    label: string;
};

// Helper type to handle the different filter types having different structures
export type FilterValues<K extends AllUnifiedFilterKeys> =
    K extends CoreUnifiedFilterKeys.TYPE
        ? TypeFilterOptionModel[]
        : BaseFilterOptionModel[];

export interface LastSearchFilter {
    selectedFilterIds: string[][];
    searchQuery: string;
}
